import { TaskParams } from '~/components/task-forms/BaseTaskForm'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { taskSubmit } from '~/services/taskService'
import { buildSimplifiedTaskRequest } from '~/utils/taskRequestBuilder'
import { BetterToast } from '~/utils/toast'

/**
 * Hook for handling task submission with JSON.
 * This is meant to be used locally within components, not globally.
 * Parent component is responsible for navigation, conversation refresh, and UI updates.
 *
 * Simple architecture: TaskForm builds AgentRequest → Convert to JSON → Send JSON → Backend builds protobuf
 */
const useTaskSubmit = () => {
  const { currentWorkspace } = useWorkspaceContext()

  const handleTaskSubmit = async (
    task: TaskParams
  ): Promise<{ taskId: number; uuid: string } | null> => {
    if (!currentWorkspace?.uuid) {
      throw new Error('Workspace not found')
    }

    try {
      console.info('[Task Submit] Converting to JSON and sending')

      // Convert protobuf AgentRequest to simple JSON
      const simplifiedRequest = buildSimplifiedTaskRequest(task)

      // Debug log for migration document tasks
      if (simplifiedRequest.command_type === 17) {
        console.log('[Task Submit] MIGRATION_DOCUMENT request:', {
          has_migration_doc_input: !!simplifiedRequest.migration_doc_input,
          migration_doc_input: simplifiedRequest.migration_doc_input
        })
      }

      // Submit task using JSON - backend creates new conversation and builds protobuf
      const taskCreated = await taskSubmit({
        workspaceUuid: currentWorkspace.uuid,
        request: simplifiedRequest,
        signal: new AbortController().signal
      })

      if (taskCreated == null) {
        task.resolve?.(false)
        return null
      }

      // Backend returns conversationUuid
      const conversationUuid = taskCreated.conversationUuid || ''

      if (!conversationUuid) {
        console.error('[Task Submit] No conversation UUID returned from backend')
        task.resolve?.(false)
        BetterToast.error('Failed to create conversation')
        return null
      }

      task.resolve?.(true)

      console.info('[Task Submit Success]', {
        taskId: taskCreated.taskId,
        conversationUuid
      })

      // Return the task information - parent handles navigation and UI updates
      return { taskId: taskCreated.taskId, uuid: conversationUuid }
    } catch (error) {
      task.resolve?.(false)

      if (error instanceof Error) {
        console.error('[Task Submit Error]', error)
        const errorType = (error as Error & { errorType?: string })?.errorType

        // These error types are handled by ModalContext via ApiForbiddenError event
        const isHandledByModalContext =
          errorType === 'workspace_read_only' ||
          errorType === 'upgrade_required' ||
          errorType === 'quota_exceeded_overage_disabled' ||
          errorType === 'billable_flows_exceeded_overage_disabled' ||
          errorType === 'insufficient_credits' ||
          error.message.includes('credit') ||
          error.message.includes('Upgrade') ||
          error.message.includes('Billable flow')

        // Only show toast for errors not handled elsewhere
        if (!isHandledByModalContext) {
          BetterToast.error(error.message)
        }
      }
      return null
    }
  }

  return { handleTaskSubmit }
}

export default useTaskSubmit
